import { useCallback, useEffect, useRef, useState } from 'react';
import { userService } from '../services/userService';
import eventService from '../services/eventService';
import privateLessonRequestService from '../services/privateLessonRequestService';

/**
 * [Web UX 4. csomag (Admin teendők), 2026-10-04] Az admin három „jóváhagyásra váró" darabszáma EGY helyen:
 * függő regisztrációk, függő asztalfoglalások, függő magánóra-igénylések. A felső „Beállítások" fül, a
 * mobil alsó sáv és a Beállítások al-fülei EBBŐL a forrásból kapják a jelvényt (a Dashboard hívja, és propként adja
 * tovább) — két független lekérés két különböző számmal nem létezhet.
 *
 * Miért kliens-oldali számlálás (a lista-végpontok hossza), nem számláló-végpont: a jelvény KONSTRUKCIÓBÓL
 * ugyanazt a halmazt számolja, amit az al-fül listája mutat (pl. a múltbeli, el nem bírált asztalfoglalás is
 * beleszámít, mert a lista is mutatja) — nem lehet „fogalom-eltérés". Mérve a lokális DB-n: 1 / 8 sor, 5–8 ms, < 3 KB.
 *
 * Miért ÖSSZEG a felső jelvény (a mobil `admin.tsx:52-67` ettől eltér: ott a magánóra külön jelvény): az
 * admin kérdése „vár-e rám valami a Beállításokban", nem az, hogy melyik al-fülön.
 *
 * NÉMA HIBA (a `Dashboard.refreshUnreadMessages` mintájára): mindhárom szám KÜLÖN őrzi az utolsó ISMERT
 * értékét; sikertelen frissítés NEM nulláz; a soha be nem töltött szám ISMERETLEN (`null`, nem 0) — így
 * sem hamis „0", sem hamis szám nem villan, és hibasáv/toast sincs.
 */
export type PendingKind = 'users' | 'reservations' | 'lessons';

export interface PendingCounts {
  users: number | null;
  reservations: number | null;
  lessons: number | null;
}

export const UNKNOWN_PENDING_COUNTS: PendingCounts = { users: null, reservations: null, lessons: null };

/** Az ISMERT számok összege; ha mind ismeretlen, 0 (a hívó 0-nál nem mutat jelvényt). */
export function pendingTotal(counts: PendingCounts): number {
  return (counts.users ?? 0) + (counts.reservations ?? 0) + (counts.lessons ?? 0);
}

export interface PendingAdminCounts {
  counts: PendingCounts;
  /** Az ismert számok összege (0 = nincs jelvény). */
  total: number;
  /** Mindhárom szám újratöltése a szerverről (néma hiba). */
  refresh: () => void;
  /** Egy gyermek-lista friss hossza (betöltés/jóváhagyás után) — elavulttá tesz minden korábban indult lekérést. */
  report: (kind: PendingKind, count: number) => void;
}

export function usePendingAdminCounts(enabled: boolean): PendingAdminCounts {
  const [counts, setCounts] = useState<PendingCounts>(UNKNOWN_PENDING_COUNTS);
  // Elavult válasz ne írjon felül újabbat: kindenként sorszám. Minden lekérés-indítás ÉS minden gyermek-jelentés
  // növeli; a válasz csak akkor érvényes, ha közben nem indult/érkezett újabb.
  const seq = useRef<Record<PendingKind, number>>({ users: 0, reservations: 0, lessons: 0 });
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const apply = useCallback((kind: PendingKind, value: number) => {
    setCounts((prev) => (prev[kind] === value ? prev : { ...prev, [kind]: value }));
  }, []);

  const refresh = useCallback(() => {
    if (!enabled) return;
    const sources: Array<[PendingKind, () => Promise<number>]> = [
      ['users', async () => (await userService.getPendingUsers()).length],
      ['reservations', async () => (await eventService.getPendingReservations()).length],
      ['lessons', () => privateLessonRequestService.getPendingCount()],
    ];
    sources.forEach(([kind, load]) => {
      const mine = ++seq.current[kind];
      // Promise.resolve().then: egy szinkron kivétel / nem-Promise visszatérés sem törheti meg a Dashboardot.
      Promise.resolve()
        .then(load)
        .then((n) => {
          if (mounted.current && seq.current[kind] === mine && typeof n === 'number' && Number.isFinite(n)) apply(kind, n);
        })
        .catch(() => {
          /* szándékosan néma: az utolsó ismert érték marad (vagy ismeretlen) */
        });
    });
  }, [enabled, apply]);

  const report = useCallback(
    (kind: PendingKind, count: number) => {
      if (!enabled) return;
      seq.current[kind]++;
      apply(kind, count);
    },
    [enabled, apply],
  );

  return { counts, total: pendingTotal(counts), refresh, report };
}
